// BYO-01 "My subscriptions": the subscription policy a member reads, and tool connections
// (program row M4-04, Work OS `connect`: the provider rules are lifted in
// `packages/domain-agents/src/connect`). Base plan: a person's own connections are an account
// setting, so `core`; what an agent may do with one is decided where the agent runs.
import { z } from "zod";
import {
  ConnectionProvider,
  SubscriptionPolicy,
  ToolConnection,
  ToolConnectionConfig,
} from "../connection-rows";
import { ToolConnectionScope } from "../enums";
import { LIMITS, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "connections" } as const;

/** Add a custom API key connection (Work OS B3): the key is sealed on receipt, never returned. */
export const ToolConnectionCreate = z
  .strictObject({
    scope: ToolConnectionScope,
    provider: z.literal("api_key"),
    label: text(LIMITS.name),
    config: ToolConnectionConfig,
    api_key: z.string().min(8).max(4096),
  })
  .meta({ id: "ToolConnectionCreate" });
export const ToolConnectionListQuery = PageQuery.extend({ scope: ToolConnectionScope.optional() });
export const ToolConnectionRevoke = z
  .strictObject({ reason: z.string().max(LIMITS.text).optional() })
  .meta({ id: "ToolConnectionRevoke" });
/** The probe's outcome: the error class only (Work OS finding 38: never the vendor's text). */
export const ToolConnectionChecked = z
  .object({
    connection: ToolConnection,
    result: z.object({
      ok: z.boolean(),
      error_class: z.string().nullable(),
      status: z.int().nullable(),
    }),
  })
  .meta({ id: "ToolConnectionChecked" });
export const ConnectionProviderParams = CompanyParams.extend({
  // GHL-PROVIDER: the GoHighLevel marketplace app's OAuth (Location or Company token)
  provider: z.enum(["google", "github", "slack", "ghl"]),
});
/** `tiers`: the provider's tier keys, comma-separated (`calendar_read,gmail_send`). */
export const OAuthStart = z.object({
  scope: ToolConnectionScope,
  tiers: z
    .string()
    .min(1)
    .max(400)
    .regex(/^[a-z_]{1,64}(,[a-z_]{1,64}){0,9}$/),
  label: z.string().min(1).max(LIMITS.name).optional(),
  expected_identity: z.string().max(LIMITS.name).optional(),
  /**
   * M4-04: sign an existing connection in again (Work OS reconnect). Same provider; the account
   * that signs in must be the same vendor account (its subject), or nothing is stored.
   */
  reconnect: Uuid.optional(),
});
export const OAuthStarted = z
  .object({ authorize_url: z.string(), expires_at: z.string() })
  .meta({ id: "OAuthStarted" });
/** The web's callback page posts the vendor's `code` and the sealed `state` it carried. */
export const OAuthComplete = z
  .strictObject({ state: z.string().min(1).max(4096), code: z.string().min(1).max(2048) })
  .meta({ id: "OAuthComplete" });

export const connectionRoutes = {
  "subscriptionPolicy.get": defineRoute({
    ...core,
    tag: "model-credentials",
    id: "subscriptionPolicy.get",
    method: "get",
    path: `${co}/subscription-policy`,
    summary: "Which credential and connection paths are open here, and why one is closed",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: SubscriptionPolicy },
    errors: [],
    emits: [],
  }),
  "connections.providers": defineRoute({
    ...core,
    id: "connections.providers",
    method: "get",
    path: `${co}/connection-providers`,
    summary: "The connection catalogue: each provider, its tiers and whether it is set up",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(ConnectionProvider) }) },
    errors: [],
    emits: [],
  }),
  "connections.list": defineRoute({
    ...core,
    id: "connections.list",
    method: "get",
    path: `${co}/connections`,
    summary: "The caller's own connections, and the company's for owners and admins",
    access: "member",
    params: CompanyParams,
    query: ToolConnectionListQuery,
    response: { status: 200, schema: page(ToolConnection) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "connections.create": defineRoute({
    ...core,
    id: "connections.create",
    method: "post",
    path: `${co}/connections`,
    summary: "Connect a tool with a custom API key (sealed on receipt, never returned)",
    access: "member",
    params: CompanyParams,
    body: ToolConnectionCreate,
    idempotency: "required",
    response: { status: 201, schema: ToolConnection },
    errors: ["connection_path_disabled"],
    emits: ["tool_connection.added"],
  }),
  "connections.check": defineRoute({
    ...core,
    id: "connections.check",
    method: "post",
    path: `${co}/connections/{id}/check`,
    summary: "Run the connection's read-only probe and record its state",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: ToolConnectionChecked },
    errors: ["connection_state_conflict", "connection_not_set_up"],
    emits: ["tool_connection.checked", "tool_connection.refreshed"],
  }),
  "connections.refresh": defineRoute({
    ...core,
    id: "connections.refresh",
    method: "post",
    path: `${co}/connections/{id}/refresh`,
    summary: "Refresh an OAuth connection's access token now (it also refreshes on its own)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: ToolConnection },
    errors: ["connection_state_conflict", "connection_not_set_up"],
    emits: ["tool_connection.refreshed"],
  }),
  "connections.revoke": defineRoute({
    ...core,
    id: "connections.revoke",
    method: "post",
    path: `${co}/connections/{id}/revoke`,
    summary: "Disconnect (the sealed secret is destroyed; nothing can use it again)",
    access: "member",
    params: CompanyItemParams,
    body: ToolConnectionRevoke,
    idempotency: "optional",
    response: { status: 200, schema: ToolConnection },
    errors: ["connection_state_conflict"],
    emits: ["tool_connection.revoked", "crew_connection_grant.changed", "connection.grant_changed"],
  }),
  "connections.oauthStart": defineRoute({
    ...core,
    id: "connections.oauthStart",
    method: "get",
    path: `${co}/connections/oauth/{provider}/authorize`,
    summary: "Start an OAuth connection: the vendor's sign-in URL with a sealed, expiring state",
    access: "member",
    params: ConnectionProviderParams,
    query: OAuthStart,
    response: { status: 200, schema: OAuthStarted },
    errors: ["connection_path_disabled", "connection_not_set_up", "connection_state_conflict"],
    emits: [],
  }),
  "connections.oauthComplete": defineRoute({
    ...core,
    id: "connections.oauthComplete",
    method: "post",
    path: `${co}/connections/oauth/complete`,
    summary:
      "Finish an OAuth connection (once per sign-in): exchange the code, check scopes and identity, seal",
    access: "member",
    params: CompanyParams,
    body: OAuthComplete,
    idempotency: "required",
    response: { status: 201, schema: ToolConnection },
    errors: ["connection_path_disabled", "connection_not_set_up", "connection_state_conflict"],
    emits: ["tool_connection.added", "tool_connection.reconnected"],
  }),
} as const;
